import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { FaBullhorn, FaExclamationTriangle, FaInfoCircle, FaTimes } from 'react-icons/fa'

import {
  ANNOUNCEMENTS_CHANGED,
  Announcement,
  dismissAnnouncement,
  getActiveAnnouncements,
} from '../../data/announcementData'
import './AnnouncementBanner.css'

// Often enough that a new announcement reaches people within minutes, without
// every open tab polling constantly.
const REFRESH_MS = 5 * 60 * 1000

const ICONS = {
  High: <FaExclamationTriangle />,
  Normal: <FaBullhorn />,
  Low: <FaInfoCircle />,
}

/**
 * Site-wide announcements, shown under the top bar on every page until the
 * user dismisses them. A dismissal is for good - the API remembers it per user.
 */
const AnnouncementBanner = () => {
  const location = useLocation()
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [dismissed, setDismissed] = useState<number[]>([])

  const load = useCallback(async () => {
    if (!localStorage.getItem('authToken')) return
    try {
      setAnnouncements(await getActiveAnnouncements())
    } catch {
      // A banner that fails to load shouldn't get in the way of the page.
    }
  }, [])

  // On arrival, on each change of page, and periodically.
  useEffect(() => {
    load()
  }, [load, location.pathname])

  useEffect(() => {
    const timer = window.setInterval(load, REFRESH_MS)
    const onFocus = () => load()
    window.addEventListener('focus', onFocus)
    window.addEventListener(ANNOUNCEMENTS_CHANGED, onFocus)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener(ANNOUNCEMENTS_CHANGED, onFocus)
    }
  }, [load])

  const dismiss = async (id: number) => {
    // Gone straight away; put back if the API didn't record it, so it isn't
    // silently lost and shown again on the next page.
    setDismissed((prev) => [...prev, id])
    try {
      await dismissAnnouncement(id)
      setAnnouncements((prev) => prev.filter((a) => a.announcementId !== id))
    } catch {
      // Left in the list, so it reappears below.
    } finally {
      setDismissed((prev) => prev.filter((d) => d !== id))
    }
  }

  const shown = announcements.filter((a) => !dismissed.includes(a.announcementId))
  if (shown.length === 0) return null

  return (
    <div className='ann-stack' role='region' aria-label='Announcements'>
      {shown.map((a) => (
        <div
          key={a.announcementId}
          className={`ann-banner ann-banner--${a.priority.toLowerCase()}`}
          role={a.priority === 'High' ? 'alert' : 'status'}
        >
          <span className='ann-banner__icon'>{ICONS[a.priority] ?? ICONS.Normal}</span>
          <div className='ann-banner__body'>
            <div className='ann-banner__title'>{a.title}</div>
            {a.message && <div className='ann-banner__message'>{a.message}</div>}
          </div>
          <button
            type='button'
            className='ann-banner__dismiss'
            onClick={() => dismiss(a.announcementId)}
            title='Dismiss'
            aria-label={`Dismiss ${a.title}`}
          >
            <FaTimes />
          </button>
        </div>
      ))}
    </div>
  )
}

export default AnnouncementBanner
